import "./App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Protected from "./Pages/protected";
import NotFound from "./Pages/pagenotfound";
import Login from "./Pages/login";
import { LayoutProvider } from "./Pages/context/layoutcontext";
import { AuthProvider } from "./Pages/context/AuthContext";
import AuthChecker from "./Pages/context/authcheck";
import { LanguageProvider } from "./Pages/context/LanguageContext"; // Import LanguageProvider
import { isFingerprintFeatureEnabled, isPlayoutFingerprintEnabled } from "./config/featureFlags";
import AppLoader, { GlobalLoader } from "./Pages/component/AppLoader";

const Signup = lazy(() => import("./Pages/signup"));
const QC = lazy(() => import("./Pages/QcGrid"));
const MissingClips = lazy(() => import("./Pages/missingClips"));
const ManualVideoUpload = lazy(() => import("./Pages/manualVideoUpload"));
// The weekly off-air-request grid. Channel Monitoring held /home briefly, got
// rolled back on 2026-09-09 over bugs since fixed in the admin dashboard's copy
// (the reference implementation), and is back on /home now that this page has
// been re-aligned with that fix. Kept reachable at /channel-dashboard so any
// saved links still land somewhere real.
const ChannelDashboard = lazy(() => import("./Pages/home"));
const ChannelMonitoring = lazy(() => import("./Pages/channelMonitoring"));
const Location = lazy(() => import("./Pages/location"));
const Channel = lazy(() => import("./Pages/channel"));
const Celebrity = lazy(() => import("./Pages/celebrity"));
const Personality = lazy(() => import("./Pages/personality"));
const Genre = lazy(() => import("./Pages/genre"));
const Advertisement = lazy(() => import("./Pages/advertisement"));
const HeadlineOld = lazy(() => import("./Pages/headline"));
const HeadlineMaster = lazy(() => import("./Pages/headlineMaster"));
const Language = lazy(() => import("./Pages/language"));
const Network = lazy(() => import("./Pages/Network"));
const Program = lazy(() => import("./Pages/program"));
const Promo = lazy(() => import("./Pages/promo"));
const AnchorMaster = lazy(() => import("./Pages/voiceMaster").then((module) => ({ default: module.AnchorMasterPage })));
const ProgramMaster = lazy(() => import("./Pages/programMaster"));
const ReporterMaster = lazy(() => import("./Pages/voiceMaster").then((module) => ({ default: module.ReporterMasterPage })));
const Substory = lazy(() => import("./Pages/substory"));
const StoryFormat = lazy(() => import("./Pages/storyformat"));
const Geography = lazy(() => import("./Pages/geography"));
const ForgotPassword = lazy(() => import("./Pages/ForgotPassword"));
const ChangePassword = lazy(() => import("./Pages/changepassword"));
const PersonalityGenre = lazy(() => import("./Pages/Genre/personalityGenre"));
const SongGenre = lazy(() => import("./Pages/Genre/songGenre"));
const ProgramGenre = lazy(() => import("./Pages/Genre/programGenre"));
const Logger = lazy(() => import("./Pages/logger"));
const BarcXML = lazy(() => import("./Pages/barcxml"));
const StoryGenre = lazy(() => import("./Pages/Genre/storyGenre"));
const QCdashboard = lazy(() => import("./Pages/qcDashboard"));
const QCForm = lazy(() => import("./Pages/qcform"));
const QCLogDashboard = lazy(() => import("./Pages/qclogs"));
const MasterLogs = lazy(() => import("./Pages/MasterLogs"));
const UserMaster = lazy(() => import("./Pages/user/UserMaster"));
const AddUser = lazy(() => import("./Pages/user/AddUser"));
const RoleMaster = lazy(() => import("./Pages/role/RoleMaster"));
const EditUser = lazy(() => import("./Pages/user/EditUser"));
const AddRole = lazy(() => import("./Pages/role/AddRole"));
const EditRole = lazy(() => import("./Pages/role/EditRole"));
const ReportPage = lazy(() => import("./Pages/report/Report"));
const FingerprintCatalog = lazy(() => import("./Pages/fingerprintCatalog"));
function App() {
  return (
    <>
      <GlobalLoader />
      <AuthProvider>
        <LanguageProvider>
          {" "}
          {/* Add LanguageProvider here */}
          <BrowserRouter>
            <LayoutProvider>
              <AuthChecker>
                <Suspense fallback={<AppLoader overlay label="Loading page" />}>
                  <Routes>
                  <Route path="/" element={<Login />} />
                  <Route path="/forgotpassword" element={<ForgotPassword />} />
                  <Route path="/changepassword" element={<ChangePassword />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/QC" element={<Protected Component={QC} />} />
                  <Route path="/missing-clips" element={<Protected Component={MissingClips} />} />
                  <Route path="/missing-clips/manual-upload" element={<Protected Component={ManualVideoUpload} />} />
                  <Route path="/manual-video-upload" element={<Protected Component={ManualVideoUpload} />} />
                  {isPlayoutFingerprintEnabled() && (
                    <Route path="/fingerprints" element={<Protected Component={FingerprintCatalog} />} />
                  )}
                  <Route
                    path="/home"
                    element={<Protected Component={ChannelMonitoring} />}
                  />
                  {/* The off-air grid's old home. Kept live, just quiet -- not
                      linked from the sidebar, so it only reaches anyone with the
                      URL already saved. */}
                  <Route
                    path="/channel-dashboard"
                    element={<Protected Component={ChannelDashboard} />}
                  />
                  {/* Same page as /home, kept so links saved while Channel
                      Monitoring lived only here (before the 2026-09-09 rollback)
                      still resolve. */}
                  <Route
                    path="/channel-monitoring"
                    element={<Protected Component={ChannelMonitoring} />}
                  />
                  {/* <Route
                    path="/user"
                    element={<Protected Component={User} />}
                  /> */}
                  <Route
                    path="/personalities"
                    element={<Protected Component={Personality} />}
                  />
                  <Route
                    path="/storyformat"
                    element={<Protected Component={StoryFormat} />}
                  />
                  <Route
                    path="/locations"
                    element={<Protected Component={Location} />}
                  />
                  <Route
                    path="/channel"
                    element={<Protected Component={Channel} />}
                  />
                  <Route
                    path="/celebrity"
                    element={<Protected Component={Celebrity} />}
                  />
                  <Route
                    path="/genre"
                    element={<Protected Component={Genre} />}
                  />
                  <Route
                    path="/advertisement"
                    element={<Protected Component={Advertisement} />}
                  />
                  {/* /headline serves the rebuilt page. The sidebar entry and
                      every existing link keep working and land on it. */}
                  <Route
                    path="/headline"
                    element={<Protected Component={HeadlineMaster} />}
                  />
                  {/* The original page, off the sidebar but still reachable --
                      it is the fallback if the new grid misbehaves in QA. */}
                  <Route
                    path="/headline-old"
                    element={<Protected Component={HeadlineOld} />}
                  />
                  <Route
                    path="/language"
                    element={<Protected Component={Language} />}
                  />
                  <Route
                    path="/network"
                    element={<Protected Component={Network} />}
                  />
                  <Route
                    path="/program"
                    element={<Protected Component={Program} />}
                  />
                  <Route
                    path="/promo"
                    element={<Protected Component={Promo} />}
                  />
                  {isFingerprintFeatureEnabled("anchors") && (
                    <Route
                      path="/anchor-master"
                      element={<Protected Component={AnchorMaster} />}
                    />
                  )}
                  {isFingerprintFeatureEnabled("reporters") && (
                    <Route
                      path="/reporter-master"
                      element={<Protected Component={ReporterMaster} />}
                    />
                  )}
                  {isFingerprintFeatureEnabled("program_name") && (
                    <Route
                      path="/program-master"
                      element={<Protected Component={ProgramMaster} />}
                    />
                  )}
                  <Route
                    path="/subStory"
                    element={<Protected Component={Substory} />}
                  />
                  <Route
                    path="/geography"
                    element={<Protected Component={Geography} />}
                  />
                  <Route
                    path="/logger"
                    element={<Protected Component={Logger} />}
                  />
                  <Route
                    path="/barcxml"
                    element={<Protected Component={BarcXML} />}
                  />

                  <Route
                    path="/personalityGenre"
                    element={<Protected Component={PersonalityGenre} />}
                  />
                  <Route
                    path="/songGenre"
                    element={<Protected Component={SongGenre} />}
                  />
                  <Route
                    path="/programGenre"
                    element={<Protected Component={ProgramGenre} />}
                  />
                  <Route
                    path="/storyGenre"
                    element={<Protected Component={StoryGenre} />}
                  />
                  {/* <Route
                    path="/report"
                    element={<Protected Component={Report} />}
                  /> */}
                  <Route
                    path="/qcDashboard"
                    element={<Protected Component={QCdashboard} />}
                  />
                  <Route
                    path="/qclogs"
                    element={<Protected Component={QCLogDashboard} />}
                  />
                  <Route
                    path="/masterlogs"
                    element={<Protected Component={MasterLogs} />}
                  />
                  <Route
                    path="/qcform"
                    element={<Protected Component={QCForm} />}
                  />
                  {/* Deep link into one substory: the form refetches the row and seeds
                      its own timeline window instead of reading the grid's navigation
                      state. Used by the Decision Trace modal's cited archive records. */}
                  <Route
                    path="/qcform/:substoryId"
                    element={<Protected Component={QCForm} />}
                  />
                  <Route
                    path="/user"
                    element={<Protected Component={UserMaster} />}
                  />
                  <Route
                    path="/addUser"
                    element={<Protected Component={AddUser} />}
                  />
                  <Route
                    path="/role"
                    element={<Protected Component={RoleMaster} />}
                  />
                  <Route
                    path="/editUser/:userId"
                    element={<Protected Component={EditUser} />}
                  />
                  <Route
                    path="/addRole"
                    element={<Protected Component={AddRole} />}
                  />
                  <Route
                    path="/editRole/:id"
                    element={<Protected Component={EditRole} />}
                  />
                  <Route
                    path="/report"
                    element={<Protected Component={ReportPage} />}
                  />

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </AuthChecker>
            </LayoutProvider>
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </>
  );
}

export default App;
